<template>
  <div class="holland-test">
    <div class="page-header">
      <h1>霍兰德职业测评</h1>
      <div style="display: flex; gap: 10px; height: 100%;align-items: top;">
        <button class="auth-btn" @click="goToSearch">首页</button>
       
      </div>
    </div>

    <div class="test-content">
      <div class="test-intro">
        <h2>霍兰德职业兴趣测评</h2>
        <p>霍兰德职业兴趣自测(SDS)是由美国职业指导专家霍兰德(John Holland)根据他本人大量的职业咨询经验及其职业类型理论编制的测评工具。</p>
        <p>该测评将职业兴趣分为现实型(R)、研究型(I)、艺术型(A)、社会型(S)、企业型(E)和常规型(C)六种类型。</p>
        <button class="start-btn" @click="startTest">开始测评</button>
      </div>

      <div v-if="testStarted && !testCompleted" class="test-questions">
        <div class="question-progress">
          <span>问题 {{ currentQuestionIndex + 1 }} / {{ questions.length }}</span>
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: progressPercentage + '%' }"></div>
          </div>
        </div>
        
        <div class="question">
          <h3>{{ currentQuestion.question }}</h3>
          <div class="answer-options">
            <button 
              v-for="(option, index) in currentQuestion.options" 
              :key="index" 
              class="answer-btn"
              @click="selectAnswer(index)"
              :class="{ 'selected': selectedAnswers[currentQuestionIndex] === index }">
              {{ option.text }}
            </button>
          </div>
        </div>

        <div class="navigation-buttons">
          <button 
            class="nav-btn" 
            :disabled="currentQuestionIndex === 0" 
            @click="previousQuestion">
            上一题
          </button>
          <button 
            class="nav-btn" 
            :disabled="selectedAnswers[currentQuestionIndex] === null"
            @click="nextQuestion">
            {{ currentQuestionIndex === questions.length - 1 ? '提交' : '下一题' }}
          </button>
        </div>
      </div>

      <div v-if="testCompleted" class="test-results">
        <h2>测评结果</h2>
        
        <!-- 六边形雷达图 -->
        <div class="radar-chart-container">
          <svg class="radar-chart" viewBox="0 0 200 200">
            <!-- 六边形网格线 -->
            <polygon 
              v-for="level in 5" 
              :key="'grid-' + level"
              :points="getGridPoints(level)"
              class="radar-grid"
            />
            
            <!-- 轴线 -->
            <line 
              v-for="(axis, index) in axes" 
              :key="'axis-' + index"
              :x1="centerX" :y1="centerY"
              :x2="axis.x" :y2="axis.y"
              class="radar-axis"
            />
            
            <!-- 数据连接线 -->
            <polygon 
              :points="getDataPoints()"
              class="radar-data-line"
            />
            
            <!-- 数据填充区域 -->
            <polygon 
              :points="getDataPoints()"
              class="radar-data-fill"
            />
            
            <!-- 类型标签 -->
            <text 
              v-for="(type, index) in ['R', 'I', 'A', 'S', 'E', 'C']" 
              :key="'label-' + type"
              :x="axes[index].x" :y="axes[index].y - 15"
              class="radar-label"
              text-anchor="middle"
            >
              {{ getTypeLabel(type) }}
            </text>
            
            <!-- 得分标签 -->
            <circle
              v-for="(score, type) in scores" 
              :key="'dot-' + type"
              :cx="getDataPoint(type).x" :cy="getDataPoint(type).y"
              r="4"
              class="radar-score-dot"
            />
            <text 
              v-for="(score, type) in scores" 
              :key="'score-' + type"
              :x="getDataPoint(type).x" :y="getDataPoint(type).y - 10"
              class="radar-score"
              text-anchor="middle"
            >
              {{ score.value }}
            </text>
          </svg>
        </div>

        <div class="recommendations">
          <h3>职业推荐</h3>
          <ul>
            <li v-for="(job, index) in recommendedJobs" :key="index">{{ job }}</li>
          </ul>
        </div>

        <div class="personality-traits">
          <h3>性格特点</h3>
          <div v-for="([type, trait], index) in filteredPersonalityTraits" :key="index">
            <h4>{{ getTypeLabel(type) }}</h4>
            <p>{{ trait }}</p>
          </div>
        </div>

        <button class="restart-btn" @click="restartTest">重新测评</button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import hollandQuestions from '../data/hollandQuestions.json';

export default {
  name: 'HollandTest',
  components: {
  },
  data() {
    // Fisher-Yates 洗牌算法
    const shuffleArray = (array) => {
      const newArray = [...array];
      for (let i = newArray.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
      }
      return newArray;
    };
    
    return {
      isLoggedIn: false,
      username: '',
      testStarted: false,
      testCompleted: false,
      currentQuestionIndex: 0,
      selectedAnswers: [],
      questions: shuffleArray(hollandQuestions),
      centerX: 100,
      centerY: 100,
      radius: 80,
      axes: [] // 六边形顶点坐标
    };
  },
  computed: {
    currentQuestion() {
      return this.questions[this.currentQuestionIndex];
    },
    progressPercentage() {
      return ((this.currentQuestionIndex + 1) / this.questions.length) * 100;
    },
    scores() {
      // 计算各类型得分
      const scores = { R: 0, I: 0, A: 0, S: 0, E: 0, C: 0 };
      
      this.selectedAnswers.forEach((answerIndex, questionIndex) => {
        if (answerIndex === null) return;
        
        const question = this.questions[questionIndex];
        const option = question.options[answerIndex];
        
        if (option.type) {
          scores[option.type] += 2 - Math.abs(answerIndex - 2); // 非常喜欢/不喜欢得2分，比较喜欢/不喜欢得1分，一般得0分
        }
      });
      
      // 计算百分比
      const maxScore = 10; // 每种类型最多5题×2分=10分
      return Object.entries(scores).reduce((acc, [type, value]) => {
        acc[type] = {
          value,
          percentage: (value / maxScore) * 100
        };
        return acc;
      }, {});
    },
    recommendedJobs() {
      // 根据得分推荐职业
      const sortedTypes = Object.entries(this.scores)
        .sort((a, b) => b[1].value - a[1].value)
        .map(entry => entry[0]);
      
      // 这里可以根据实际需求扩展职业推荐列表
      const jobRecommendations = {
        R: ['工程师', '技术员', '机械师', '木匠', '厨师'],
        I: ['科学家', '研究员', '程序员', '医生', '数学家'],
        A: ['艺术家', '设计师', '音乐家', '作家', '演员'],
        S: ['教师', '护士', '咨询师', '社会工作者', '教练'],
        E: ['企业家', '经理', '销售人员', '政治家', '律师'],
        C: ['会计', '秘书', '行政人员', '银行职员', '图书管理员']
      };
      
      // 推荐得分最高的三种类型的职业
      return [...new Set(
        sortedTypes.slice(0, 3).flatMap(type => jobRecommendations[type])
      )];
    },
    personalityTraits() {
      // 各类型的性格特点
      return {
        R: '现实型的人喜欢具体的任务，愿意使用工具从事操作性工作，动手能力强，做事手脚灵活，动作协调。偏好于具体任务，不善言辞，做事保守，较为谦虚。缺乏社交能力，通常喜欢独立做事。',
        I: '研究型的人是思想家而非实干家，抽象思维能力强，求知欲强，肯动脑，善思考，不愿动手。喜欢独立的和富有创造性的工作。知识渊博，有学识才能，不善于领导他人。考虑问题理性，做事喜欢精确，喜欢逻辑分析和推理，不断探讨未知的领域。',
        A: '艺术型的人有创造力，乐于创造新颖、与众不同的成果，渴望表现自己的个性，实现自身的价值。做事理想化，追求完美，不重实际。具有一定的艺术才能和个性。善于表达，怀旧，心态较为复杂。',
        S: '社会型的人喜欢与人交往、不断结交新的朋友、善言谈、愿意教导别人。关心社会问题、渴望发挥自己的社会作用。寻求广泛的人际关系，比较看重社会义务和社会道德。',
        E: '企业型的人追求权力、权威和物质财富，具有领导才能。喜欢竞争、敢冒风险、有野心、抱负。为人务实，习惯以利益得失，权利、地位、金钱等来衡量做事的价值，做事有较强的目的性。',
        C: '常规型的人尊重权威和规章制度，喜欢按计划办事，细心、有条理，习惯接受他人的指挥和领导，自己不谋求领导职务。喜欢关注实际和细节情况，通常较为谨慎和保守，缺乏创造性，不喜欢冒险和竞争，富有自我牺牲精神。'
      };
    },
    filteredPersonalityTraits() {
      // 只显示得分较高的类型的性格特点
      return Object.entries(this.personalityTraits).filter(([type]) => {
        return this.scores[type].value > 5; // 得分超过5分才显示
      });
    }
  },
  mounted() {
    this.calculateAxes();
  },
  methods: {
    // 计算六边形顶点坐标
    calculateAxes() {
      const angles = [60, 0, 300, 240, 180, 120]; // 六个顶点的角度
      this.axes = angles.map(angle => {
        const radian = (angle * Math.PI) / 180;
        return {
          x: this.centerX + this.radius * Math.cos(radian),
          y: this.centerY - this.radius * Math.sin(radian)
        };
      });
    },
    
    // 获取网格点坐标
    getGridPoints(level) {
      const scale = level / 5;
      return this.axes.map(axis => {
        const x = this.centerX + (axis.x - this.centerX) * scale;
        const y = this.centerY + (axis.y - this.centerY) * scale;
        return `${x},${y}`;
      }).join(' ');
    },
    
    // 获取单个数据点坐标
    getDataPoint(type) {
      const index = ['R', 'I', 'A', 'S', 'E', 'C'].indexOf(type);
      if (index === -1) return { x: this.centerX, y: this.centerY };
      
      const score = this.scores[type];
      const scale = score.percentage / 100;
      const axis = this.axes[index];
      
      return {
        x: this.centerX + (axis.x - this.centerX) * scale,
        y: this.centerY + (axis.y - this.centerY) * scale
      };
    },
    
    // 获取所有数据点坐标
    getDataPoints() {
      return ['R', 'I', 'A', 'S', 'E', 'C'].map(type => {
        const point = this.getDataPoint(type);
        return `${point.x},${point.y}`;
      }).join(' ');
    },
    
    // 获取类型标签
    getTypeLabel(type) {
      const labels = {
        'R': '现实型',
        'I': '研究型',
        'A': '艺术型',
        'S': '社会型',
        'E': '企业型',
        'C': '常规型'
      };
      return labels[type] || type;
    },
    
    // 其他现有方法...
    startTest() {
      this.testStarted = true;
      this.selectedAnswers = new Array(this.questions.length).fill(null);
    },
    
    selectAnswer(optionIndex) {
      this.selectedAnswers[this.currentQuestionIndex] = optionIndex;
      // 强制更新UI以确保下一题按钮状态正确
      this.$forceUpdate();
    },
    
    previousQuestion() {
      if (this.currentQuestionIndex > 0) {
        this.currentQuestionIndex--;
      }
    },
    
    nextQuestion() {
      if (this.selectedAnswers[this.currentQuestionIndex] !== null) {
        if (this.currentQuestionIndex === this.questions.length - 1) {
          this.testCompleted = true;
        } else {
          this.currentQuestionIndex++;
        }
      }
    },
    
    restartTest() {
      this.testStarted = false;
      this.testCompleted = false;
      this.currentQuestionIndex = 0;
      this.selectedAnswers = [];
    },
    
    goToLogin() {
      // 导航到登录页面
      this.$router.push('/login');
    },
    
    goToRegister() {
      // 导航到注册页面
      this.$router.push('/register');
    },
    
    goToSearch() {
      // 跳转到首页
      this.$router.push('/');
    },
    
    logout() {
      // 调用后端API进行登出
      axios.post('api/logout/')
        .then(() => {
          // 退出成功后，更新本地状态
          this.isLoggedIn = false;
          this.username = '';
          // 清除本地存储
          localStorage.removeItem('csrftoken');
          localStorage.removeItem('user');
          // 跳转到首页
          this.$router.push('/');
        })
        .catch(error => {
          console.error('退出登录失败:', error);
          // 即使出错，也清除前端状态
          this.isLoggedIn = false;
          this.username = '';
          localStorage.removeItem('csrftoken');
          localStorage.removeItem('user');
          this.$router.push('/');
        });
    }
  }
};
</script>

<style scoped>
.holland-test {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 10px;
  border-bottom: 2px solid #eee;
}

.auth-btn {
  padding: 8px 16px;
  background-color: #4CAF50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.auth-btn:hover {
  background-color: #45a049;
}

.username {
  background-color: #2196F3;
}

.username:hover {
  background-color: #0b7dda;
}

.test-content {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.test-intro {
  text-align: center;
  max-width: 800px;
  margin-bottom: 50px;
}

.start-btn {
  padding: 12px 24px;
  background-color: #f44336;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  margin-top: 20px;
}

.start-btn:hover {
  background-color: #da190b;
}

.test-questions {
  max-width: 800px;
  width: 100%;
}

.question-progress {
  margin-bottom: 30px;
}

.progress-bar {
  width: 100%;
  height: 10px;
  background-color: #eee;
  border-radius: 5px;
  margin-top: 10px;
}

.progress-fill {
  height: 100%;
  background-color: #4CAF50;
  border-radius: 5px;
  transition: width 0.3s ease;
}

.question {
  margin-bottom: 30px;
  text-align: center;
}

.answer-options {
  display: flex;
  justify-content: center;
  gap: 15px;
  flex-wrap: wrap;
  margin-top: 20px;
}

.answer-btn {
  padding: 10px 20px;
  border: 1px solid #ddd;
  background-color: white;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.answer-btn:hover {
  background-color: #f0f0f0;
}

.answer-btn.selected {
  background-color: #e8f4ff;
  border-color: #4a67fb;
}

.answer-btn:disabled {
  background-color: #e8f4ff;
  border-color: #4CAF50;
  cursor: not-allowed;
}

.navigation-buttons {
  display: flex;
  justify-content: space-between;
  margin-top: 40px;
}

.nav-btn {
  padding: 10px 20px;
  background-color: #2196F3;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.nav-btn:hover:not(:disabled) {
  background-color: #0b7dda;
}

.nav-btn:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.test-results {
  max-width: 800px;
  width: 100%;
  text-align: center;
}

.results-summary {
  margin-bottom: 40px;
}

/* 雷达图样式 */
.radar-chart-container {
  width: 100%;
  max-width: 500px;
  margin: 0 auto 30px;
}

.radar-chart {
  width: 100%;
  height: auto;
}

.radar-grid {
  fill: none;
  stroke: #e0e0e0;
  stroke-width: 1;
}

.radar-axis {
  stroke: #666;
  stroke-width: 1.5;
}

.radar-data-line {
  fill: none;
  stroke: #3498db;
  stroke-width: 2;
}

.radar-data-fill {
  fill: #3498db;
  fill-opacity: 0.3;
}

.radar-label {
  font-size: 12px;
  font-weight: bold;
  fill: #333;
}

.radar-score {
  font-size: 11px;
  font-weight: bold;
  fill: #333;
}

.radar-score-dot {
  fill: #3498db;
  stroke: white;
  stroke-width: 1;
}

.recommendations, .personality-traits {
  margin-bottom: 30px;
}

.recommendations h3, .personality-traits h3 {
  color: #333;
  margin-bottom: 20px;
  border-bottom: 1px solid #ddd;
  padding-bottom: 10px;
}

.recommendations ul {
  list-style-type: disc;
  padding-left: 20px;
  /* 修改为多列网格布局 */
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 10px 20px;
}

.recommendations li {
  margin-bottom: 10px;
  color: #666;
  /* 确保列表项在网格中正确对齐 */
  align-self: start;
}

.personality-traits h4 {
  margin-top: 20px;
  color: #333;
}

.restart-btn {
  padding: 12px 24px;
  background-color: #f44336;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
}

.restart-btn:hover {
  background-color: #da190b;
}
</style>